'use client'

import { useState } from 'react'
import { trackEvent } from '@/lib/blog/analytics'
import { BLOG_CONSTANTS } from '@/lib/blog/config'
import { siteConfig } from '@/config/site'

type ShareProvider = 'x' | 'linkedin' | 'copy'

interface ShareButtonsProps {
  slug: string
  title: string
}

export function ShareButtons({ slug, title }: ShareButtonsProps) {
  const [copyFeedback, setCopyFeedback] = useState(false)
  const url = `${siteConfig.baseUrl}/blog/${slug}`

  function handleShare(provider: Exclude<ShareProvider, 'copy'>) {
    trackEvent('share_click', { provider, slug })

    const urls = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    }

    window.open(urls[provider], '_blank', 'noopener,noreferrer')
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(url)
      setCopyFeedback(true)
      trackEvent('share_click', { provider: 'copy' satisfies ShareProvider, slug })
      setTimeout(
        () => setCopyFeedback(false),
        BLOG_CONSTANTS.SHARE_FEEDBACK_DURATION_MS
      )
    } catch {
      // Clipboard API not available
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-[var(--color-border-default)]">
      <span className="text-sm font-medium text-[var(--color-text-muted)]">
        Share
      </span>

      {/* X / Twitter */}
      <button
        type="button"
        onClick={() => handleShare('x')}
        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-bg-elevated)] px-3 py-2 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:opacity-80"
        aria-label="Share on X"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        X
      </button>

      {/* LinkedIn */}
      <button
        type="button"
        onClick={() => handleShare('linkedin')}
        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-bg-elevated)] px-3 py-2 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:opacity-80"
        aria-label="Share on LinkedIn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
        </svg>
        LinkedIn
      </button>

      {/* Copy Link */}
      <button
        type="button"
        onClick={handleCopyLink}
        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          copyFeedback
            ? 'bg-[var(--color-success)] text-white'
            : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] hover:opacity-80'
        }`}
        aria-label="Copy link to article"
      >
        {copyFeedback ? 'Copied' : 'Copy Link'}
      </button>
    </div>
  )
}
